import { useQuery } from '@tanstack/react-query';
import { useForBindingId } from '../../../forBinding';
import { RecommendationCorrection } from '../../../recommendations/types';
import api from '../../http';

interface correctionResponse {
	docs: RecommendationCorrection[];
	totalDocs: number;
	limit: number;
	totalPages: number;
	page: number;
	pagingCounter: number;
	hasPrevPage: boolean;
	hasNextPage: boolean;
	prevPage: number | null;
	nextPage: number | null;
	ownerName: string;
}

interface UseGetAdviceCorrectionParams {
	page?: number;
	limit?: number;
	areaId?: string | string[];
	startDate?: string;
	endDate?: string;
	filter?: string;
	sortAsc?: string | boolean;
	enabled?: boolean;
}

export function useGetAdviceCorrection({
	page = 1,
	limit = 200,
	areaId,
	startDate,
	endDate,
	filter,
	sortAsc,
	enabled = true,
}: UseGetAdviceCorrectionParams = {}) {
	const forBinding = useForBindingId();
	const query = useQuery({
		queryKey: ['correction', forBinding, page, limit, areaId, startDate, endDate, filter, sortAsc],
		enabled,
		placeholderData: (previousData, previousQuery) =>
			previousQuery?.queryKey[1] === forBinding ? previousData : undefined,
		queryFn: async () => {
			const params = new URLSearchParams();
			params.set('page', String(page));
			params.set('limit', String(limit));

			if (forBinding) params.set('forBinding', forBinding);
			if (startDate) params.set('startDate', startDate);
			if (endDate) params.set('endDate', endDate);
			if (filter) params.set('filter', filter);
			if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));

			const areaIds = Array.isArray(areaId) ? areaId : areaId ? [areaId] : [];
			for (const id of areaIds) params.append('areaId', id);

			const { data } = await api.get<correctionResponse>('/advices/planting/findAll', {
				params,
			});
			return data;
		},
	});

	return {
		...query,
		data: query.data?.docs ?? [],
		pagination: query.data
			? {
					totalDocs: query.data.totalDocs,
					totalPages: query.data.totalPages,
					page: query.data.page,
					hasPrevPage: query.data.hasPrevPage,
					hasNextPage: query.data.hasNextPage,
					prevPage: query.data.prevPage,
					nextPage: query.data.nextPage,
				}
			: null,
	};
}
